import log from "loglevel";
import { watch } from "vue";
import { isEqual } from "lodash";
import { defineStore } from "pinia";

import { useUserStore } from "./UserStore";

import { PayoutService } from "@/api/services/PayoutService";

import { Paging } from "@/interfaces/Paging";
import { Country } from "@/interfaces/Payouts/Country";
import { PayoutData } from "@/interfaces/Payouts/PayoutData";
import { Denomination } from "@/interfaces/Payouts/Denomination";
import { PayoutAPIResponse } from "@/interfaces/Payouts/PayoutAPIResponse";
import { MyPayoutDataResponse } from "@/interfaces/Payouts/MyPayoutDataResponse";
import { PayoutOptionDenominations } from "@/interfaces/Payouts/PayoutOptionDenominations";
import { InitiatePayoutAPIResponse } from "@/interfaces/Payouts/InitiatePayoutAPIResponse";

export const PayoutStore = defineStore("PayoutStore", {
  state: () => ({

    MyPayoutDataPaging: {
      count: 24,
      offset: 0,
      page: 1,
      totalPages: 1,
    } as Paging,
    MyPayoutData: [] as PayoutData[],
    loadingMyPayoutData: false as boolean,

    SelectedMyPayoutData: {} as PayoutData,

    SuccessResponsePayoutData: {} as PayoutData,
    FailureResponseMessage: "" as string,
    ProcessingPayout: false as boolean,

    SelectedDenomination: {} as Denomination,
    SelectedPayoutOption: {} as PayoutOptionDenominations,

    PayoutOptionsAndTheirDenominationsPaging: {
      count: 24,
      offset: 0,
      page: 1,
      totalPages: 1,
    } as Paging,
    PayoutOptionsAndTheirDenominations: [] as PayoutOptionDenominations[],
    loadingPayoutOptionsAndTheirDenominations: false as boolean,

    PayoutOptionCountries: [] as Country[],
    PayoutOptionCurrentCountry: {} as Country,
  }),

  actions: {





		
    async initialize() {
      try {
        await this.getInitData();
        this.setupWatchers();
        return true;
      } catch (error) {
        return false;
      }
    },







    async getInitData() {
      await this.getMyPayoutData();
      await this.getAllPayoutOptions();
    },








    async getMyPayoutData() {
      this.loadingMyPayoutData = true;

      try {
        const myPayoutDataResponse: MyPayoutDataResponse = await PayoutService.getMyPayoutData(this.MyPayoutDataPaging);
        this.MyPayoutData = myPayoutDataResponse?.data ?? [];
        this.MyPayoutDataPaging.totalPages = myPayoutDataResponse?.paging?.totalPages ?? 1;
      } catch (error) {
        log.error('Failed to get payout data:', error);
        // Don't throw the error, just log it
      }
      finally {
        this.loadingMyPayoutData = false;
      }

    },








    async getAllPayoutOptions() {
      this.loadingPayoutOptionsAndTheirDenominations = true;

      try {
        const response: PayoutAPIResponse = await PayoutService.getAllPayoutOptions(this.PayoutOptionsAndTheirDenominationsPaging, this.PayoutOptionCurrentCountry);
        this.PayoutOptionCountries = response?.countries ?? [];
        this.PayoutOptionsAndTheirDenominations = response?.data ?? [];
        this.PayoutOptionCurrentCountry = response?.current_country ?? {} as Country;
        this.PayoutOptionsAndTheirDenominationsPaging.totalPages = response?.paging?.totalPages ?? 1;
      } catch (error) {
        log.error('Failed to get payout options:', error);
        // Don't throw the error, just log it
      }
      finally {
        this.loadingPayoutOptionsAndTheirDenominations = false;
      }
    },







    setSelectedMyPayoutOption(myPayoutDataId: number) {
      const optionToSet = this.MyPayoutData.find(
        (option) => option.id === myPayoutDataId
      );
      if (optionToSet) {
        this.SelectedMyPayoutData = optionToSet;
      }
    },







    setSelectedPayoutOption(optionId: number) {
      const optionToSet = this.PayoutOptionsAndTheirDenominations.find(
        (option) => option.option.id === optionId
      );
      if (optionToSet) {
        this.SelectedPayoutOption = optionToSet;
      }
    },







    setSelectedDenomination(denominationId: number) {
      if (
        this.SelectedPayoutOption &&
        this.SelectedPayoutOption.denominations
      ) {
        const denominationToSet = this.SelectedPayoutOption.denominations.find(
          (denomination) => denomination.id === denominationId
        );
        if (denominationToSet) {
          this.SelectedDenomination = denominationToSet;
        } else {
          log.error("Denomination with the provided ID was not found.");
        }
      } else {
        log.error(
          "No payout option is selected, or the selected payout option does not have any denominations."
        );
      }
    },







    
    async initiatePayout() {
      this.ProcessingPayout = true;
      const SelectedDenominationId = this.SelectedDenomination.id;
      const selectedPayoutOptionId = this.SelectedPayoutOption.option.id;

      const userStore = useUserStore();

      const email = userStore.user.email;

      try {
        const response: InitiatePayoutAPIResponse =
          await PayoutService.initiatePayout(
            selectedPayoutOptionId,
            SelectedDenominationId,
            email
          );
        userStore.syncUser(response.user);
        this.SuccessResponsePayoutData = response.payout_data;
        window.dataLayer.push({
          event: "redeemReward",
          reward: {
            url: window.location.href,
            path: location.pathname,
            provider: response.payout_data.provider,
            name: response.payout_data.payout_option.name,
            payout_id: response.payout_data.payout_option_id,
            user_id: response.payout_data.user_id,
            usd_amount: response.payout_data.usd_amount,
            currency_amount: response.payout_data.currency_amount,
            currency: response.payout_data.currency,
            coins_amount: response.payout_data.currency_amount,
          },
        });
        this.getMyPayoutData();
      } catch (error: any) {
        this.FailureResponseMessage = error.response.data.error.message;
      } finally {
        this.ProcessingPayout = false;
      }
    },

    setupWatchers() {
      watch(
        () => this.SelectedPayoutOption,
        (newValue) => {
          if (
            newValue &&
            newValue.denominations &&
            newValue.denominations.length > 0
          ) {
            this.SelectedDenomination = newValue.denominations[0];
          }
        },
        { immediate: true }
      );
      watch(
        () => this.MyPayoutDataPaging.page,
        (newValue: number) => {
          this.MyPayoutDataPaging.offset =
            (newValue - 1) * this.MyPayoutDataPaging.count;
          this.getMyPayoutData();
        }
      );
      watch(
        () => this.PayoutOptionsAndTheirDenominationsPaging.page,
        (newValue: number) => {
          this.PayoutOptionsAndTheirDenominationsPaging.offset =
            (newValue - 1) *
            this.PayoutOptionsAndTheirDenominationsPaging.count;
          this.getAllPayoutOptions();
        }
      );
      watch(
        () => this.PayoutOptionCurrentCountry,
        (newValue: Country, oldValue: Country) => {
          if (!isEqual(newValue, oldValue)) {
            if (this.PayoutOptionsAndTheirDenominationsPaging.page === 1)
              this.getAllPayoutOptions();
            this.setPayoutOptionsPage(1);
          }
        }
      );
    },

    setMyPayoutsPage(page: number) {
      this.MyPayoutDataPaging.page = page;
    },

    setPayoutOptionsPage(page: number) {
      this.PayoutOptionsAndTheirDenominationsPaging.page = page;
    },

    changeCountryForPayoutOptions(country: Country) {
      this.PayoutOptionCurrentCountry = country;
    },

    clearStoreData() {
      this.$reset();
    },
  },
});
